import {DotLottieReact} from '@lottiefiles/dotlottie-react'
import * as Dialog from '@radix-ui/react-dialog'
import {announce} from '@react-aria/live-announcer'
import {startOfMonth} from 'date-fns'

import CloseSvg from '@/assets/icons/close.svg?react'
import priceHistorySvg from '@/assets/icons/price-history.svg'
import loading from '@/assets/lotties/loading.lottie'
import {USD_DISPLAY_DECIMALS} from '@/constants/config'
import {useCurrencySymbol} from '@/lib/locale/country'
import {useLocale} from '@/lib/locale/locale'
import {isSupportedRetailer, type Product, Retailer} from '@/models/productModel'
import {getPriceHistory} from '@/services/productService'
import VisuallyHidden from '@/utils/a11y/VisuallyHidden'
import {formatLocaleDateTime} from '@/utils/datetime'

import style from './PriceHistory.module.scss'

const PriceChart = lazy(async () => import('./PriceChart'))

export interface Data {
  id: string
  title: string
  data: {x: Date; y: number}[]
}

function groupByMonth(data: [Date, number][]) {
  if (data.length === 0) {
    throw new Error('No data provided')
  }

  const groupedData = new Map<string, number[]>()
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- guaranteed to be defined above
  const lastValue = data[data.length - 1]![1]

  data.forEach(([date, value]) => {
    const month = startOfMonth(date)
    const monthKey = month.toISOString()
    if (!groupedData.has(monthKey)) {
      groupedData.set(monthKey, [])
    }
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- its guaranteed to be defined
    groupedData.set(monthKey, groupedData.get(monthKey)!.concat(value))
  })

  const meanGroupedData: [Date, number][] = []

  groupedData.forEach((value, key) => {
    meanGroupedData.push([
      new Date(key),
      value.reduce((accumulator, currentValue) => accumulator + currentValue, 0) / value.length,
    ])
  })

  // Last month value should be the latest value not mean value
  meanGroupedData[meanGroupedData.length - 1] = [
    // @ts-expect-error guaranteed to be defined
    meanGroupedData[meanGroupedData.length - 1][0],
    lastValue,
  ]

  return meanGroupedData
}

const Loading = memo(function Loading() {
  return (
    <DotLottieReact
      src={loading}
      autoplay
      loop
      className={style.Loading}
      aria-label='Loading data...'
    />
  )
})

function combineData(data: Data[]) {
  const combinedData = new Map<number, {id: string; value: number}[]>()

  data.forEach(({id, data: values}) => {
    values.forEach(({x, y}) => {
      const xNum = x.getTime()

      if (!combinedData.has(xNum)) {
        combinedData.set(xNum, [])
      }
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- its guaranteed to be defined
      combinedData.get(xNum)!.push({id, value: y})
    })
  })

  return new Map(Array.from(combinedData.entries()).map(data => [new Date(data[0]), data[1]]))
}

export default memo<Pick<Product, 'id' | 'title' | 'retailer'>>(function PriceHistory({
  id,
  title,
  retailer,
}) {
  const [data, setData] = useState<Data[]>([])
  const [isError, setIsError] = useState(false)
  const currencySymbol = useCurrencySymbol()
  const [locale] = useLocale()

  if (!id) return null
  if (!isSupportedRetailer(retailer)) return null
  if (retailer !== Retailer.Amazon) return null // Only Amazon has price history

  const showPriceHistory = function (productId: Product['id'], retailer: Product['retailer']) {
    announce('Loading price history')
    setIsError(false)
    void getPriceHistory({
      productId,
      retailer,
      locale,
    }).then(data => {
      if (data.length > 0) {
        setData([
          {
            id: retailer,
            title: retailer,
            data: groupByMonth(data)
              .slice(-12)
              .map(([x, y]) => ({
                x,
                y,
              })),
          },
          // Dumb data for testing
          // {
          //   id: 'dumb',
          //   data: groupByMonth(data)
          //     .slice(-12)
          //     .map(([x, y]) => ({
          //       x,
          //       y: y + Math.random() * 100,
          //     })),
          // },
        ])
        announce(`Price history loaded for ${title}`)
      } else {
        if (data.length === 0) {
          setIsError(true)
        }
        announce(`Cannot load price history for ${title}, please try again later.`)
      }
    })
  }

  const combinedData = combineData(data)
  const ids = data.map(data => data.id)

  return (
    <Dialog.Root
      onOpenChange={open => {
        if (open) showPriceHistory(id, retailer)
      }}
    >
      <Dialog.Trigger asChild>
        <button className={style.PriceHistoryBtn} aria-label={`View price history for ${title}`}>
          <img src={priceHistorySvg} alt='' />
        </button>
      </Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Overlay className={style.DialogOverlay} />
        <Dialog.Content className={style.DialogContent}>
          <Dialog.Close asChild>
            <button className={style.CloseButton}>
              <CloseSvg aria-hidden />
              <VisuallyHidden>Close {title} Price History</VisuallyHidden>
            </button>
          </Dialog.Close>
          <Dialog.Title className={style.DialogTitle}>
            <span aria-hidden>Price History</span>
            <VisuallyHidden>{title} Price History</VisuallyHidden>
          </Dialog.Title>
          {/* TODO: make chart accessible*/}
          {data.length > 0 ? (
            <>
              <Suspense fallback={<Loading />}>
                <div className={style.DialogChart} aria-hidden>
                  <PriceChart data={data} />
                </div>
              </Suspense>
              <VisuallyHidden as='table' isStrict>
                <thead>
                  <tr>
                    <th>Date</th>
                    {ids.map(id => (
                      <th key={id}>{`${id}'s price`}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {Array.from(combinedData.entries())
                    .reverse()
                    .map(([date, values]) => (
                      <tr key={date.getTime()}>
                        <td>
                          {formatLocaleDateTime(date, 'en-US', {
                            year: 'numeric',
                            month: 'long',
                          })}
                        </td>
                        {values.map(({id, value}) => (
                          <td key={id}>{currencySymbol + value.toFixed(USD_DISPLAY_DECIMALS)}</td>
                        ))}
                      </tr>
                    ))}
                </tbody>
              </VisuallyHidden>
            </>
          ) : isError ? (
            'Please try again later!'
          ) : (
            <Loading />
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
})
